/**
 * A seção "Novo lead" e o acordeão "Quem mais indica", só para o administrador.
 *
 * Porte do markup de `admin.html:1806-1851` e da lógica de `admin.html:7513-7626`: a máscara do
 * WhatsApp, o "Indicado por" que aparece e some com a origem, o nome novo para "Nenhum dos acima",
 * os gift cards vigentes, e o "Adicionar lead" com os mesmos textos de status.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// As peças da plataforma.
import { Botao, Campo, CampoTexto, IconeChevron, Selecao, SelecaoMultipla } from '@complexity/ui';
// A fonte opaca e as gravações do novo lead.
import type { FonteDeDados } from '@studiolu/dados';
import { criarIndicadorExterno, criarLead } from '@studiolu/dados/areas/vendas';
// A linha de status do legado (`#status-lead`), peça comum.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// As origens, na ordem do `<select>` do formulário.
import { ORDEM_ORIGENS_NO_FORMULARIO, ORIGENS_LEAD } from './constantes.js';
// A regra pura.
import {
  aplicarMascaraWhatsapp,
  construirOpcoesIndicadoPor,
  giftCardVigente,
  resolverIndicador,
  rotuloGiftCard,
  textoIndicacoes,
  type ColunasDoIndicador,
  type OpcaoIndicador,
} from './regras.js';
// A memória e as ações da tela.
import type { AcoesDoQuadro, MemoriaDoQuadro } from './quadro.js';

/** O estado do ranking: ainda não carregou, falhou, ou carregou (vazio ou com linhas). */
export type EstadoDoRanking =
  | { readonly tipo: 'inicial' }
  | { readonly tipo: 'erro'; readonly mensagem: string }
  | { readonly tipo: 'pronto'; readonly linhas: readonly (readonly [string, number])[] };

export interface NovoLeadProps {
  readonly dados: FonteDeDados;
  readonly memoria: MemoriaDoQuadro;
  readonly acoes: AcoesDoQuadro;
  readonly ranking: EstadoDoRanking;
  // Troca quando o legado chamaria `popularListaGiftCardsLead()`: a lista redesenha e a seleção zera.
  readonly versaoGiftCards: number;
}

// As opções de origem do formulário, montadas uma vez.
const OPCOES_ORIGEM = ORDEM_ORIGENS_NO_FORMULARIO.map((valor) => ({ valor, rotulo: ORIGENS_LEAD[valor] ?? valor }));
// A última opção do "Indicado por", fixa no legado (`admin.html:1827`).
const NENHUM_DOS_ACIMA: OpcaoIndicador = { valor: 'novo', rotulo: 'Nenhum dos acima' };

export function NovoLead({ dados, memoria, acoes, ranking, versaoGiftCards }: NovoLeadProps) {
  const [nome, setNome] = useState('');
  const [contato, setContato] = useState('');
  // "Por Indicação" é a primeira opção, e por isso a escolhida ao abrir.
  const [origem, setOrigem] = useState('indicacao');
  // As opções do "Indicado por", repovoadas só quando a origem vira indicação (`admin.html:7527`).
  const [opcoesIndicador, setOpcoesIndicador] = useState<OpcaoIndicador[]>(() =>
    construirOpcoesIndicadoPor(memoria.clientes, memoria.externos),
  );
  const [indicadoPor, setIndicadoPor] = useState('');
  const [indicadoNovo, setIndicadoNovo] = useState('');
  const [giftSel, setGiftSel] = useState<readonly string[]>([]);
  const [observacoes, setObservacoes] = useState('');
  const [status, setStatus] = useState('');

  // A lista de gift cards redesenhada perde a seleção, como o `innerHTML` do legado (`admin.html:6553`).
  useEffect(() => setGiftSel([]), [versaoGiftCards]);

  // Os vigentes na hora do desenho (`admin.html:6548`).
  const disponiveis = memoria.giftCards.filter((gc) => giftCardVigente(gc, new Date()));

  // Trocar a origem mostra ou esconde, e LIMPA, o "Indicado por" (`admin.html:7524-7537`).
  function trocarOrigem(nova: string) {
    setOrigem(nova);
    if (nova === 'indicacao') {
      // Repovoa mantendo o valor atual, como `popularSelectIndicadoPor` (`admin.html:7514-7521`).
      setOpcoesIndicador(construirOpcoesIndicadoPor(memoria.clientes, memoria.externos));
    } else {
      setIndicadoPor('');
      setIndicadoNovo('');
    }
  }

  // O "Adicionar lead" (`admin.html:7580-7626`), passo a passo.
  async function adicionar() {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) {
      setStatus('Preenche pelo menos o nome.');
      return;
    }
    setStatus('Salvando...');
    let colunas: ColunasDoIndicador = { indicado_por_user_id: null, indicado_por_externo_id: null };
    if (origem === 'indicacao') {
      const resolucao = resolverIndicador(indicadoPor, indicadoNovo, memoria.externos);
      if (resolucao.tipo === 'cadastrar') {
        const criado = await criarIndicadorExterno(dados, resolucao.nome);
        if (!criado.ok) {
          setStatus('Erro ao salvar indicador: ' + criado.erro.mensagem);
          return;
        }
        memoria.externos.push(criado.dados);
        colunas = { indicado_por_user_id: null, indicado_por_externo_id: criado.dados.id };
      } else {
        colunas = resolucao.colunas;
      }
    }
    const criado = await criarLead(dados, {
      nome: nomeLimpo,
      contato_whatsapp: contato.trim(),
      origem,
      status: 'novo',
      observacoes: observacoes.trim(),
      ...colunas,
    });
    if (!criado.ok) {
      setStatus('Erro ao salvar: ' + criado.erro.mensagem);
      return;
    }
    // A seleção é lida ANTES de a lista redesenhar (`admin.html:7610`).
    const selecionados = giftSel;
    setStatus('Lead adicionado!');
    // Limpa nome, WhatsApp, observações e indicador; a origem fica (`admin.html:7613-7618`).
    setNome('');
    setContato('');
    setObservacoes('');
    setIndicadoPor('');
    setIndicadoNovo('');
    await acoes.carregarLeads();
    await acoes.carregarRanking();
    if (selecionados.length) {
      await acoes.resgatarGiftCards(criado.dados.id, selecionados);
      acoes.popularGiftCards();
    }
  }

  return (
    <section className="flex flex-col gap-cx-4">
      <h2 className="text-cx-xl font-semibold text-texto">Novo lead</h2>

      {/* Os campos curtos em linha (`.linha-compacta`, `admin.html:1811`). */}
      <div className="flex flex-wrap items-end gap-cx-3">
        <Campo rotulo="Nome" autoComplete="name" value={nome} onChange={(evento) => setNome(evento.target.value)} />
        <Campo
          rotulo="WhatsApp"
          autoComplete="tel"
          inputMode="tel"
          placeholder="(99) 99999-9999"
          maxLength={15}
          value={contato}
          // A máscara a cada tecla, sobre o valor inteiro (`admin.html:6505-6507`).
          onChange={(evento) => setContato(aplicarMascaraWhatsapp(evento.target.value))}
        />
        <div className="min-w-[12rem]">
          <Selecao rotulo="Origem" opcoes={OPCOES_ORIGEM} value={origem} onChange={(evento) => trocarOrigem(evento.target.value)} />
        </div>
        {origem === 'indicacao' && (
          <div className="min-w-[12rem]">
            <Selecao
              rotulo="Indicado por"
              vazio="(selecione)"
              opcoes={[...opcoesIndicador, NENHUM_DOS_ACIMA]}
              value={indicadoPor}
              onChange={(evento) => setIndicadoPor(evento.target.value)}
            />
          </div>
        )}
        {origem === 'indicacao' && indicadoPor === 'novo' && (
          <Campo
            rotulo="Nome de quem indicou"
            autoComplete="name"
            placeholder="Digite o nome..."
            value={indicadoNovo}
            onChange={(evento) => setIndicadoNovo(evento.target.value)}
          />
        )}
      </div>

      {/* Os gift cards vigentes (`admin.html:1834-1837`, `6545-6562`). */}
      {disponiveis.length ? (
        <SelecaoMultipla
          rotulo="Gift Card(s) (opcional)"
          opcoes={disponiveis.map((gc) => ({ valor: String(gc.id), rotulo: rotuloGiftCard(gc) }))}
          valores={giftSel}
          aoMudar={setGiftSel}
        />
      ) : (
        <div className="flex flex-col gap-cx-1">
          <p className="text-cx-sm font-medium text-texto-suave">Gift Card(s) (opcional)</p>
          <p className="text-cx-xs text-texto-fraco">Nenhum gift card disponível.</p>
        </div>
      )}

      <CampoTexto rotulo="Observações" linhas={2} value={observacoes} onChange={(evento) => setObservacoes(evento.target.value)} />

      <div>
        <Botao onClick={() => void adicionar()}>Adicionar lead</Botao>
      </div>
      <LinhaDeStatus texto={status} testid="status-lead" />

      {/* "Quem mais indica", fechado por padrão; o cabeçalho abre e fecha (`admin.html:1843-1851`). */}
      <details className="group rounded-cx-cartao border border-borda bg-superficie-elevada">
        <summary className="flex min-h-toque cursor-pointer list-none items-center justify-between gap-cx-2 px-cx-4 text-cx-base font-medium text-texto">
          <span>Quem mais indica</span>
          {/* A seta desenhada, no lugar do caractere do legado, que a plataforma não aceita. */}
          <IconeChevron aria-hidden="true" className="h-cx-4 w-cx-4 text-texto-fraco transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t border-borda px-cx-4 py-cx-3" data-ranking>
          {ranking.tipo === 'erro' && <p className="text-cx-sm text-texto-suave">Erro ao carregar: {ranking.mensagem}</p>}
          {ranking.tipo === 'pronto' && ranking.linhas.length === 0 && (
            <p className="text-cx-sm text-texto-suave">Nenhuma indicação registrada ainda.</p>
          )}
          {ranking.tipo === 'pronto' &&
            ranking.linhas.map(([quem, quantidade]) => (
              <div key={quem} className="flex min-h-toque items-center justify-between gap-cx-3 border-b border-borda last:border-0">
                <span className="text-cx-base text-texto">{quem}</span>
                <span className="text-cx-sm font-semibold text-texto-suave">{textoIndicacoes(quantidade)}</span>
              </div>
            ))}
        </div>
      </details>
    </section>
  );
}
